import { useSyncExternalStore } from 'react';
import type { I18nKey, I18nTranslations } from '@/generated/i18n-keys';
import {
  i18nTranslations,
  type I18nLocale,
} from '@/generated/i18n-translations';
import { hasNativeJSBridge } from './bridge-runtime';

type I18nParams = Record<string, string | number>;

let translations: Partial<I18nTranslations> = {};
let currentTranslate = (key: I18nKey, params?: I18nParams) => t(key, params);
let initializedSource: 'browser' | 'native-bridge' | undefined;

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => currentTranslate;

export function setTranslations(nextTranslations: Partial<I18nTranslations>) {
  translations = nextTranslations;
  currentTranslate = (key: I18nKey, params?: I18nParams) => t(key, params);
  listeners.forEach(listener => listener());
}

const availableLocales = Object.keys(i18nTranslations) as I18nLocale[];
const localeByLowercase = new Map(
  availableLocales.map(locale => [locale.toLowerCase(), locale])
);
const fallbackLocale: I18nLocale = (localeByLowercase.get('en') ??
  availableLocales[0]) as I18nLocale;

function normalizeLanguageCode(language: string | null | undefined) {
  return language?.trim().replace(/_/g, '-') ?? '';
}

function resolveChineseScriptLocale(language: string) {
  const parts = language.split('-');
  if (parts[0]?.toLowerCase() !== 'zh') {
    return undefined;
  }

  const scriptOrRegion = parts[1]?.toLowerCase();
  if (
    scriptOrRegion === 'hant' ||
    (scriptOrRegion && ['hk', 'mo', 'tw'].includes(scriptOrRegion))
  ) {
    return localeByLowercase.get('zh-hant');
  }

  return localeByLowercase.get('zh-hans');
}

export function resolveI18nLocale(
  language: string | null | undefined
): I18nLocale {
  const normalizedLanguage = normalizeLanguageCode(language);
  if (!normalizedLanguage) {
    return fallbackLocale;
  }

  const exactLocale = localeByLowercase.get(normalizedLanguage.toLowerCase());
  if (exactLocale) {
    return exactLocale;
  }

  const chineseScriptLocale = resolveChineseScriptLocale(normalizedLanguage);
  if (chineseScriptLocale) {
    return chineseScriptLocale;
  }

  const baseLanguage = normalizedLanguage.split('-')[0];
  const baseLocale = localeByLowercase.get(baseLanguage.toLowerCase());
  return baseLocale ?? fallbackLocale;
}

function browserLanguage() {
  if (typeof navigator === 'undefined') {
    return fallbackLocale;
  }

  return navigator.languages?.[0] ?? navigator.language ?? fallbackLocale;
}

function beginInitialization(source: 'browser' | 'native-bridge') {
  if (initializedSource) {
    if (initializedSource !== source) {
      console.warn(
        `i18n already initialized from ${initializedSource}; ignoring ${source} initialization.`
      );
    }
    return false;
  }

  initializedSource = source;
  return true;
}

export function setTranslationsForLanguage(
  language: string | null | undefined
) {
  const locale = resolveI18nLocale(language);
  setTranslations(i18nTranslations[locale]);
  return locale;
}

export function initBrowserI18n(
  language: string | null | undefined = browserLanguage()
) {
  if (!beginInitialization('browser')) {
    return;
  }

  setTranslationsForLanguage(language);
}

export function initNativeBridgeI18n() {
  if (!beginInitialization('native-bridge')) {
    return;
  }

  if (!hasNativeJSBridge()) {
    return;
  }

  const bridge = window.jsb?.UtilsBridge;

  bridge
    ?.getTranslations()
    .then(nextTranslations => {
      if (nextTranslations) {
        setTranslations(nextTranslations as Partial<I18nTranslations>);
      }
    })
    .catch(error => {
      console.warn('Failed to load WebView translations', error);
    });

  bridge?.onSetTranslations(nextTranslations => {
    if (nextTranslations) {
      setTranslations(nextTranslations as Partial<I18nTranslations>);
    }
  });
}

export function t(key: I18nKey, params?: I18nParams): string {
  let value = translations[key] ?? key;

  if (!params) {
    return value;
  }

  for (const [paramKey, paramValue] of Object.entries(params)) {
    value = value.replaceAll(`{${paramKey}}`, String(paramValue));
  }

  return value;
}

export function useT() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
